import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { signIn, clearError } from '@/store/authSlice';
import type { AppDispatch } from '@/store';
import {
  isAllowedEmail,
  requestPasswordReset,
  resetPassword,
  startAppleOAuth,
  startGoogleOAuth,
  takeOAuthError,
} from '@/auth/nexusAuth';
import './Auth.css';

function LoginPage() {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { isLoading, error } = useSelector((state: { auth: { isLoading: boolean; error: string | null } }) => state.auth);

  const [mode, setMode] = useState<'signin' | 'forgot'>('signin');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState<'email' | 'reset'>('email');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(takeOAuthError());
  const [busy, setBusy] = useState(false);

  const sessionExpiredMessage = (location.state as { message?: string } | null)?.message;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setNotice(null);
    dispatch(clearError());
    await dispatch(signIn({ email, password }));
  };

  const requestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (isAllowedEmail(email.trim())) {
        await requestPasswordReset(email.trim());
      }
      setNotice('If that email has an account, we sent a 6-digit reset code.');
      setForgotStep('reset');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Could not request a reset');
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (newPassword.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    if (!isAllowedEmail(email.trim())) {
      setLocalError('This account is not authorized for R.U.S.H.');
      return;
    }
    setBusy(true);
    try {
      await resetPassword(email.trim(), code.trim(), newPassword);
      setMode('signin');
      setShowPasswordForm(true);
      setNotice('Password reset. You can sign in now.');
      setPassword('');
      setCode('');
      setNewPassword('');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Could not reset password');
    } finally {
      setBusy(false);
    }
  };

  const shownError = localError || error;

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1 className="auth-title">R.U.S.H.</h1>
        <p className="auth-subtitle">
          {mode === 'forgot' ? 'Reset your password' : 'Resource Utilization & Strategic Harvest'}
        </p>

        {sessionExpiredMessage && (
          <div className="error-message session-expired-message">{sessionExpiredMessage}</div>
        )}
        {shownError && <div className="error-message">{shownError}</div>}
        {notice && <div className="success-message"><p>{notice}</p></div>}

        {mode === 'forgot' ? (
          forgotStep === 'email' ? (
            <form onSubmit={requestReset} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" />
              </div>
              <button type="submit" disabled={busy} className="auth-button">{busy ? 'Sending...' : 'Send reset code'}</button>
              <button type="button" className="auth-text-button" onClick={() => { setMode('signin'); setLocalError(null); setNotice(null); }}>Back to sign in</button>
            </form>
          ) : (
            <form onSubmit={doReset} className="auth-form">
              <div className="form-group">
                <label htmlFor="code">Reset code</label>
                <input id="code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="123456" required />
              </div>
              <div className="form-group">
                <label htmlFor="new-password">New password</label>
                <input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required autoComplete="new-password" />
              </div>
              <button type="submit" disabled={busy || code.length !== 6} className="auth-button">{busy ? 'Resetting...' : 'Reset password'}</button>
              <button type="button" className="auth-text-button" onClick={() => { setMode('signin'); setLocalError(null); setNotice(null); }}>Back to sign in</button>
            </form>
          )
        ) : (
          <>
            <button type="button" className="oauth-button google" onClick={startGoogleOAuth}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.1 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.8 6.1C12.2 13.2 17.6 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-2.8-.4-4H24v7.6h12.6c-.3 2.1-1.6 5.3-4.7 7.4l7.2 5.6c4.3-4 6.9-9.9 6.9-16.6z" />
                <path fill="#FBBC05" d="M10.3 28.6A14.4 14.4 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.5 10.7l7.8-6.1z" />
                <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.2-5.6c-2 1.4-4.6 2.3-8.7 2.3-6.4 0-11.8-3.7-13.7-9l-7.8 6.1C6.4 42.6 14.6 48 24 48z" />
              </svg>
              Sign in with Google
            </button>
            <button type="button" className="oauth-button apple" onClick={startAppleOAuth}>
              <svg width="16" height="18" viewBox="0 0 14 17" aria-hidden="true">
                <path fill="currentColor" d="M13.2 5.7c-.1.1-1.9 1.1-1.9 3.3 0 2.6 2.3 3.5 2.3 3.5s-1.6 4.6-3.8 4.6c-1 0-1.8-.7-2.8-.7s-1.8.7-2.9.7C2.1 17.1.2 12.8.2 9.3.2 6 2.3 4.3 4.3 4.3c1.1 0 2 .7 2.8.7.8 0 1.9-.8 3.3-.8 1.1 0 2.2.6 2.8 1.5zM9.6 2.9c.6-.7 1-1.7.9-2.7-1 .1-2.1.6-2.7 1.4-.6.7-1.1 1.7-.9 2.6 1 .1 2.1-.5 2.7-1.3z" />
              </svg>
              Sign in with Apple
            </button>

            <div className="auth-divider">or email &amp; password</div>

            {!showPasswordForm ? (
              <button type="button" className="auth-text-button" onClick={() => setShowPasswordForm(true)}>
                Use an email &amp; password instead
              </button>
            ) : (
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="password-input-wrapper">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={isLoading} className="auth-button">
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </button>
                <button type="button" className="auth-text-button" onClick={() => { setMode('forgot'); setForgotStep('email'); setLocalError(null); setNotice(null); }}>
                  Forgot password?
                </button>
              </form>
            )}
          </>
        )}

        <p className="auth-footer">Registration is not open yet.</p>
      </div>
    </div>
  );
}

export default LoginPage;
